import type { AxiosInstance } from 'axios'
import type {
  ContentAssetCreatePayload,
  ContentAssetLinkPayload,
  ContentAssetListQuery,
  ContentAssetUpdatePayload,
  IContentAsset,
  IFactoryFunctionRequest,
  IFactoryResponse,
} from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'contentAssets'

const normalizeDate = (value?: Date | string | null) => value ? new Date(value) : null

const normalizeContentAsset = (asset: IContentAsset): IContentAsset => ({
  ...asset,
  _created: new Date(asset._created),
  _updated: new Date(asset._updated),
  expiresAt: normalizeDate(asset.expiresAt),
  linkedAt: normalizeDate(asset.linkedAt),
  deletedAt: normalizeDate(asset.deletedAt),
})

const list = (axios: IFactoryFunctionRequest) => (query: ContentAssetListQuery = {}): IFactoryResponse<IContentAsset[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    query,
    afterReceivingHook: (assets: IContentAsset[]) => assets.map(normalizeContentAsset),
  })
}

const show = (axios: IFactoryFunctionRequest) => (assetId: string): IFactoryResponse<IContentAsset> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}`,
    afterReceivingHook: normalizeContentAsset,
  })
}

const create = (axios: IFactoryFunctionRequest) => (data: ContentAssetCreatePayload): IFactoryResponse<IContentAsset, ContentAssetCreatePayload> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}`,
    data,
    afterReceivingHook: normalizeContentAsset,
  })
}

const uploadFile = (axios: IFactoryFunctionRequest) => (file: File): IFactoryResponse<IContentAsset, FormData> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('fileDescription', file.name)

  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/upload`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    afterReceivingHook: normalizeContentAsset,
  })
}

const uploadVideoFile = (axios: IFactoryFunctionRequest) => (file: File): IFactoryResponse<IContentAsset, FormData> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('fileDescription', file.name)

  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/video-upload`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
    afterReceivingHook: normalizeContentAsset,
  })
}

const createDirectUploadUrl = (axios: IFactoryFunctionRequest) => (data: {
  originalFileName: string
  fileType: string
  fileSize: number
  fileDescription?: string | null
}): IFactoryResponse<{ storageKey: string; uploadUrl: string | null; kind: 'video' }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/direct-upload-url`,
    data,
  })
}

const completeDirectUpload = (axios: IFactoryFunctionRequest) => (data: {
  storageKey: string
  originalFileName: string
  fileType: string
  fileSize: number
  fileDescription?: string | null
  title?: string | null
  alt?: string | null
  metadata?: Record<string, any> | null
  duration?: number | null
}): IFactoryResponse<IContentAsset> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/direct-upload-complete`,
    data,
    afterReceivingHook: normalizeContentAsset,
  })
}

const link = (axios: IFactoryFunctionRequest) => (assetId: string, data: ContentAssetLinkPayload): IFactoryResponse<IContentAsset, ContentAssetLinkPayload> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}/link`,
    data,
    afterReceivingHook: normalizeContentAsset,
  })
}

const update = (axios: IFactoryFunctionRequest) => (assetId: string, data: ContentAssetUpdatePayload): IFactoryResponse<IContentAsset, ContentAssetUpdatePayload> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}`,
    data,
    afterReceivingHook: normalizeContentAsset,
  })
}

const remove = (axios: IFactoryFunctionRequest) => (assetId: string): IFactoryResponse<{ _id: string; deleted: boolean }> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}`,
  })
}

const download = (axios: IFactoryFunctionRequest) => (assetId: string): IFactoryResponse<Blob> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}/download`,
    responseType: 'blob',
  })
}

const previewPdf = (axios: IFactoryFunctionRequest) => (assetId: string): IFactoryResponse<Blob> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${assetId}/preview-pdf`,
    responseType: 'blob',
  })
}

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  show: show(useAxios(axios)),
  create: create(useAxios(axios)),
  uploadFile: uploadFile(useAxios(axios)),
  uploadVideoFile: uploadVideoFile(useAxios(axios)),
  createDirectUploadUrl: createDirectUploadUrl(useAxios(axios)),
  completeDirectUpload: completeDirectUpload(useAxios(axios)),
  link: link(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
  download: download(useAxios(axios)),
  previewPdf: previewPdf(useAxios(axios)),
})
